import {
  areaRadial,
  curveCatmullRom,
  curveCatmullRomClosed,
  curveLinear,
  curveLinearClosed,
  lineRadial,
  type CurveFactory,
} from 'd3';
import { AXIS_INK_TONE, GRID_INK_TONE, GRID_WIDTH } from '../render/gridStroke';
import { toAngularTicks, toRadialTicks } from './radialAxis';
import {
  polarToScreen,
  toDistance,
  toSeriesStyle,
  type LegendItem,
  type RadialLayout,
  type ScreenPoint,
  type SeriesStyle,
} from './radialLayout';
import type {
  RadialMarkType,
  RadialPlot,
  RadialPoint,
  RadialSeries,
} from './types';

const TICK_LABEL_FONT = '11px system-ui, -apple-system, "Segoe UI", sans-serif';
const TITLE_FONT = '600 12px system-ui, -apple-system, "Segoe UI", sans-serif';
export const LEGEND_FONT = TICK_LABEL_FONT;

const TAIL_TONE = 0.55;
const DIMMED_ALPHA = 0.2;
const AREA_ALPHA = 0.16;
const LINE_WIDTH = 2;
const HOVERED_LINE_WIDTH = 3;
const VERTEX_RADIUS = 2.5;
const HOVERED_VERTEX_RADIUS = 5;
const HALO_WIDTH = 3;

export type RadialHover = { seriesIndex: number; pointIndex: number };

export type RadialRenderArgs = {
  context: CanvasRenderingContext2D;
  plot: RadialPlot;
  layout: RadialLayout;
  markType: RadialMarkType;
  hovered: RadialHover | null;
  format: (value: number) => string;
  surfaceColor: string;
  inkColor: string;
};

const isSpline = (markType: RadialMarkType): boolean =>
  markType === 'spline' || markType === 'splineArea';

const isArea = (markType: RadialMarkType): boolean =>
  markType === 'area' || markType === 'splineArea';

const toCurve = (markType: RadialMarkType, isClosed: boolean): CurveFactory => {
  if (isSpline(markType)) {
    return isClosed ? curveCatmullRomClosed : curveCatmullRom;
  }
  return isClosed ? curveLinearClosed : curveLinear;
};

/** Starts a closed series just after its first gap, so the edge across twelve o'clock survives. */
export const toOpenSequence = (points: RadialPoint[]): RadialPoint[] => {
  const firstGap = points.findIndex(({ value }) => value === null);
  if (firstGap === -1) return points;
  return [...points.slice(firstGap + 1), ...points.slice(0, firstGap + 1)];
};

export type SeriesPath = {
  points: RadialPoint[];
  isClosed: boolean;
};

/** Every series closes back to its first vertex, unless a gap breaks it. */
export const toSeriesPath = (series: RadialSeries): SeriesPath => {
  const hasGap = series.points.some(({ value }) => value === null);
  if (hasGap) return { points: toOpenSequence(series.points), isClosed: false };
  return { points: series.points, isClosed: true };
};

const toColor = (style: SeriesStyle, inkColor: string): string =>
  style.kind === 'slot' ? style.color : inkColor;

const toTone = (style: SeriesStyle): number =>
  style.kind === 'slot' ? 1 : TAIL_TONE;

const toSpokeAngles = (plot: RadialPlot): number[] => {
  const { angularAxis } = plot;
  return angularAxis.kind === 'categorical'
    ? angularAxis.spokes.map(({ angle }) => angle)
    : toAngularTicks(angularAxis.axis).map(({ angle }) => angle);
};

const traceRing = (
  context: CanvasRenderingContext2D,
  center: ScreenPoint,
  distance: number,
  spokeAngles: number[] | null
): void => {
  context.beginPath();
  if (!spokeAngles) {
    context.arc(center.x, center.y, distance, 0, Math.PI * 2);
    return;
  }
  spokeAngles.forEach((angle, index) => {
    const { x, y } = polarToScreen(center, distance, angle);
    if (index === 0) context.moveTo(x, y);
    else context.lineTo(x, y);
  });
  context.closePath();
};

const renderGrid = ({
  context,
  plot,
  layout,
  format,
  surfaceColor,
  inkColor,
}: RadialRenderArgs): void => {
  const { center, radius, angularLabels } = layout;
  const spokeAngles = toSpokeAngles(plot);
  const polygonal =
    plot.angularAxis.kind === 'categorical' && spokeAngles.length >= 3
      ? spokeAngles
      : null;
  const ticks = toRadialTicks(plot.radialAxis);
  const [min, max] = plot.radialAxis.range;

  context.save();
  context.strokeStyle = inkColor;
  context.lineWidth = GRID_WIDTH;

  ticks.forEach(value => {
    const distance = toDistance(plot, radius, value);
    if (distance <= 0) return;
    context.globalAlpha =
      value === max || value === 0 ? AXIS_INK_TONE : GRID_INK_TONE;
    traceRing(context, center, distance, polygonal);
    context.stroke();
  });

  context.globalAlpha = GRID_INK_TONE;
  spokeAngles.forEach(angle => {
    const { x, y } = polarToScreen(center, radius, angle);
    context.beginPath();
    context.moveTo(center.x, center.y);
    context.lineTo(x, y);
    context.stroke();
  });

  context.globalAlpha = 1;
  context.fillStyle = inkColor;
  context.font = TICK_LABEL_FONT;
  context.textAlign = 'left';
  context.textBaseline = 'middle';
  context.strokeStyle = surfaceColor;
  context.lineWidth = HALO_WIDTH;
  context.lineJoin = 'round';
  ticks
    .filter(value => value !== min || min !== 0)
    .forEach(value => {
      const text = format(value);
      const y = center.y - toDistance(plot, radius, value);
      context.strokeText(text, center.x + 4, y);
      context.fillText(text, center.x + 4, y);
    });

  angularLabels.forEach(({ text, x, y, align, baseline }) => {
    context.textAlign = align;
    context.textBaseline = baseline;
    context.fillText(text, x, y);
  });

  context.restore();
};

export type SeriesPaint = {
  color: string;
  lineAlpha: number;
  areaAlpha: number;
  lineWidth: number;
};

export type SeriesPaintArgs = {
  seriesIndex: number;
  hovered: RadialHover | null;
  inkColor: string;
};

export const toSeriesPaint = ({
  seriesIndex,
  hovered,
  inkColor,
}: SeriesPaintArgs): SeriesPaint => {
  const style = toSeriesStyle(seriesIndex);
  const isHovered = hovered?.seriesIndex === seriesIndex;
  const emphasis = hovered === null || isHovered ? 1 : DIMMED_ALPHA;
  const lineAlpha = toTone(style) * emphasis;
  return {
    color: toColor(style, inkColor),
    lineAlpha,
    areaAlpha: AREA_ALPHA * lineAlpha,
    lineWidth: isHovered ? HOVERED_LINE_WIDTH : LINE_WIDTH,
  };
};

type PathTracer = {
  context: CanvasRenderingContext2D;
  path: SeriesPath;
  curve: CurveFactory;
  distanceOf: (point: RadialPoint) => number;
};

const isDefined = (point: RadialPoint): boolean => point.value !== null;

const traceArea = ({ context, path, curve, distanceOf }: PathTracer): void => {
  if (path.isClosed) {
    lineRadial<RadialPoint>()
      .angle(({ angle }) => angle)
      .radius(distanceOf)
      .curve(curve)
      .context(context)(path.points);
    return;
  }
  areaRadial<RadialPoint>()
    .angle(({ angle }) => angle)
    .innerRadius(0)
    .outerRadius(distanceOf)
    .defined(isDefined)
    .curve(curve)
    .context(context)(path.points);
};

const traceLine = ({ context, path, curve, distanceOf }: PathTracer): void => {
  lineRadial<RadialPoint>()
    .angle(({ angle }) => angle)
    .radius(distanceOf)
    .defined(isDefined)
    .curve(curve)
    .context(context)(path.points);
};

type VertexRenderArgs = {
  context: CanvasRenderingContext2D;
  series: RadialSeries;
  hoveredPointIndex: number | null;
  distanceOf: (point: RadialPoint) => number;
  paint: SeriesPaint;
  surfaceColor: string;
};

const renderVertices = ({
  context,
  series,
  hoveredPointIndex,
  distanceOf,
  paint,
  surfaceColor,
}: VertexRenderArgs): void =>
  series.points.forEach((point, pointIndex) => {
    if (point.value === null) return;
    const { x, y } = polarToScreen(
      { x: 0, y: 0 },
      distanceOf(point),
      point.angle
    );
    const isHoveredPoint = pointIndex === hoveredPointIndex;
    context.beginPath();
    context.arc(
      x,
      y,
      isHoveredPoint ? HOVERED_VERTEX_RADIUS : VERTEX_RADIUS,
      0,
      Math.PI * 2
    );
    context.fillStyle = isHoveredPoint ? surfaceColor : paint.color;
    context.fill();
    if (!isHoveredPoint) return;
    context.lineWidth = LINE_WIDTH;
    context.stroke();
  });

export type SeriesRenderArgs = RadialRenderArgs & {
  series: RadialSeries;
  seriesIndex: number;
};

export const renderSeries = ({
  context,
  plot,
  layout,
  markType,
  hovered,
  inkColor,
  surfaceColor,
  series,
  seriesIndex,
}: SeriesRenderArgs): void => {
  const paint = toSeriesPaint({ seriesIndex, hovered, inkColor });
  const path = toSeriesPath(series);
  const tracer: PathTracer = {
    context,
    path,
    curve: toCurve(markType, path.isClosed),
    distanceOf: point => toDistance(plot, layout.radius, point.value ?? 0),
  };

  context.save();
  context.translate(layout.center.x, layout.center.y);
  context.strokeStyle = paint.color;
  context.fillStyle = paint.color;
  context.lineJoin = 'round';

  if (isArea(markType)) {
    context.globalAlpha = paint.areaAlpha;
    context.beginPath();
    traceArea(tracer);
    context.fill();
  }

  context.globalAlpha = paint.lineAlpha;
  context.lineWidth = paint.lineWidth;
  context.beginPath();
  traceLine(tracer);
  context.stroke();

  renderVertices({
    context,
    series,
    hoveredPointIndex:
      hovered?.seriesIndex === seriesIndex ? hovered.pointIndex : null,
    distanceOf: tracer.distanceOf,
    paint,
    surfaceColor,
  });

  context.restore();
};

const renderLegend = (
  context: CanvasRenderingContext2D,
  legend: LegendItem[],
  inkColor: string
): void => {
  context.save();
  context.font = LEGEND_FONT;
  context.textAlign = 'left';
  context.textBaseline = 'middle';
  legend.forEach(({ label, style, x, y }) => {
    context.globalAlpha = toTone(style);
    context.fillStyle = toColor(style, inkColor);
    context.fillRect(x, y + 3, 10, 10);
    context.globalAlpha = 1;
    context.fillStyle = inkColor;
    context.fillText(label, x + 16, y + 8);
  });
  context.restore();
};

const renderCaptions = (
  context: CanvasRenderingContext2D,
  captions: RadialLayout['captions'],
  inkColor: string
): void => {
  context.save();
  context.font = TITLE_FONT;
  context.fillStyle = inkColor;
  context.textAlign = 'left';
  context.textBaseline = 'top';
  captions.forEach(({ text, x, y }) => context.fillText(text, x, y));
  context.restore();
};

/** Painter's order: the tail first, then the slotted series, then whichever series is hovered. */
export const toDrawOrder = (
  seriesCount: number,
  hovered: RadialHover | null
): number[] =>
  Array.from({ length: seriesCount }, (_, index) => seriesCount - 1 - index)
    .filter(index => index !== hovered?.seriesIndex)
    .concat(hovered ? [hovered.seriesIndex] : []);

export const renderRadial = (args: RadialRenderArgs): void => {
  const { context, plot, layout, inkColor, hovered } = args;
  if (layout.radius <= 0) return;

  renderGrid(args);
  toDrawOrder(plot.series.length, hovered).forEach(seriesIndex =>
    renderSeries({ ...args, series: plot.series[seriesIndex], seriesIndex })
  );
  renderLegend(context, layout.legend, inkColor);
  renderCaptions(context, layout.captions, inkColor);
};
